import React, { useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Divider,
} from '@mui/material';
import CloseIcon        from '@mui/icons-material/Close';
import AutoAwesomeIcon  from '@mui/icons-material/AutoAwesome';
import AiChartRenderer  from './AiChartRenderer';
import InsightSummary   from './InsightSummary';
import { markdownToHtml, isRawMarkdown } from './narrativeUtils';

// ─── Compact data table for preview ──────────────────────────────────────────
const DataTable = ({ data }) => {
    if (!data || data.length === 0) return null;
    const keys = Object.keys(data[0]);
    const formatVal = (v) => {
        if (v === null || v === undefined) return '—';
        const n = parseFloat(v);
        return isNaN(n) ? String(v) : (n % 1 === 0 ? n.toLocaleString() : n.toFixed(2));
    };
    return (
        <TableContainer sx={{ borderRadius: 1, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
            <Table size="small">
                <TableHead>
                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                        {keys.map(k => (
                            <TableCell key={k} sx={{
                                fontWeight: 700, fontSize: 11, py: 0.75, px: 1.5,
                                color: '#475569', borderBottom: '2px solid #e2e8f0',
                                whiteSpace: 'nowrap',
                            }}>
                                {k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.slice(0, 20).map((row, i) => (
                        <TableRow key={i} sx={{
                            '&:nth-of-type(odd)': { bgcolor: '#fafafa' },
                            '&:last-child td': { border: 0 },
                        }}>
                            {keys.map(k => (
                                <TableCell key={k} sx={{ fontSize: 11.5, py: 0.6, px: 1.5, color: '#1e293b' }}>
                                    {formatVal(row[k])}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

// ─── Report-tone card — no badge, no widget border, plain document style ──────
const ReportCard = ({ item, layout }) => {
    const isChart    = !!item.viz_config_json;
    const isFullWide = layout === 'full';

    // Resolve narrative to HTML
    const rawNarrative = item.narrative || '';
    const narrativeHtml = rawNarrative
        ? (isRawMarkdown(rawNarrative) ? markdownToHtml(rawNarrative) : rawNarrative)
        : '';

    const isTable = !isChart && item.result_json?.length > 0;

    return (
        <Box sx={{
            bgcolor: '#ffffff',
            borderRadius: 0,
            // Report-style: only a very subtle bottom separator, no card border
            borderBottom: '1px solid #e2e8f0',
            pb: 3,
            mb: 1,
            width: '100%',
        }}>
            {/* Section title — styled like a report heading, not a chip */}
            <Typography sx={{
                fontSize: isFullWide ? 15 : 14,
                fontWeight: 700,
                color: '#1e293b',
                mb: 0.75,
                lineHeight: 1.35,
                letterSpacing: '-0.01em',
            }}>
                {item.title || 'Untitled'}
            </Typography>

            {/* Content */}
            {isChart ? (
                <Box sx={{ mt: 1.5 }}>
                    <AiChartRenderer
                        data={item.result_json || []}
                        vizConfig={item.viz_config_json}
                        height={isFullWide ? 300 : 240}
                    />
                </Box>
            ) : narrativeHtml ? (
                <Box
                    dangerouslySetInnerHTML={{ __html: narrativeHtml }}
                    sx={{
                        fontSize: 13,
                        lineHeight: 1.8,
                        color: '#334155',
                        '& p': { margin: '0 0 8px 0' },
                        '& ul, & ol': { pl: 2.5, mt: 0.5, mb: 1, listStyle: 'disc' },
                        '& li': { mb: '4px' },
                        '& strong, & b': { fontWeight: 700, color: '#1e293b' },
                        '& h2, & h3, & h4': {
                            fontSize: '13px', fontWeight: 700, color: '#6366f1',
                            mt: '12px', mb: '4px',
                        },
                        '& span[style*="color:#16a34a"]': { color: '#16a34a !important' },
                        '& span[style*="color:#dc2626"]': { color: '#dc2626 !important' },
                    }}
                />
            ) : isTable ? (
                <Box sx={{ mt: 1 }}>
                    <DataTable data={item.result_json} />
                </Box>
            ) : (
                <Typography variant="caption" sx={{ color: '#94a3b8', fontStyle: 'italic' }}>
                    No content available.
                </Typography>
            )}
        </Box>
    );
};

// ─── Main dialog ──────────────────────────────────────────────────────────────
const ReportPreviewDialog = ({ open, onClose, reportItems = [], reportTitle }) => {
    const today = new Date().toLocaleDateString('en-US', {
        year: 'numeric', month: 'long', day: 'numeric',
    });

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    height: '92vh',
                    borderRadius: 2,
                    bgcolor: '#ffffff',
                    overflow: 'hidden',
                    boxShadow: '0 25px 60px rgba(0,0,0,0.18)',
                },
            }}
        >
            {/* ── Dialog title bar ── */}
            <DialogTitle sx={{
                m: 0, p: 0,
                display: 'flex', alignItems: 'center',
                borderBottom: '1px solid #e2e8f0',
                bgcolor: '#ffffff',
            }}>
                <Box sx={{ px: 2.5, py: 1.25, display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                    <AutoAwesomeIcon sx={{ color: '#6366f1', fontSize: 18 }} />
                    <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>
                        Report Preview
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, color: '#94a3b8', ml: 0.5 }}>
                        — {reportItems.length} section{reportItems.length !== 1 ? 's' : ''}
                    </Typography>
                </Box>
                <IconButton
                    onClick={onClose}
                    sx={{ mr: 1, color: '#94a3b8', '&:hover': { color: '#f43f5e', bgcolor: 'rgba(244,63,94,0.08)' } }}
                >
                    <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
            </DialogTitle>

            {/* ── Report document body ── */}
            <DialogContent sx={{
                p: 0,
                overflowY: 'auto',
                bgcolor: '#ffffff',
                '&::-webkit-scrollbar': { width: 5 },
                '&::-webkit-scrollbar-thumb': { bgcolor: '#cbd5e1', borderRadius: 3 },
            }}>
                {reportItems.length === 0 ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <Typography sx={{ color: '#94a3b8', fontSize: 14 }}>
                            No items pinned to the report yet.
                        </Typography>
                    </Box>
                ) : (
                    /* ── A4-like document area ── */
                    <Box sx={{
                        maxWidth: 820,
                        mx: 'auto',
                        px: { xs: 3, sm: 5 },
                        py: 4,
                    }}>
                        {/* Report header */}
                        <Box sx={{ mb: 3.5, pb: 2.5, borderBottom: '3px solid #6366f1' }}>
                            <Typography sx={{
                                fontSize: 22, fontWeight: 800, color: '#0f172a',
                                letterSpacing: '-0.02em', lineHeight: 1.25,
                            }}>
                                {reportTitle || 'Agentic Insight Report'}
                            </Typography>
                            <Typography sx={{ fontSize: 12, color: '#94a3b8', mt: 0.75 }}>
                                Generated {today} · {reportItems.length} section{reportItems.length !== 1 ? 's' : ''}
                            </Typography>
                        </Box>

                        {/* ── Layout grid ── */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', mx: -2 }}>
                            {reportItems.map((item, i) => {
                                const isHalf = item.layout_width === 'half';
                                return (
                                    <Box key={item.id || i} sx={{ 
                                        width: isHalf ? '50%' : '100%', 
                                        px: 2, 
                                        mb: 1 
                                    }}>
                                        <ReportCard item={item} layout={isHalf ? 'half' : 'full'} />
                                    </Box>
                                );
                            })}
                        </Box>

                        {/* Report footer */}
                        <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e2e8f0', textAlign: 'center' }}>
                            <Typography sx={{ fontSize: 10.5, color: '#cbd5e1' }}>
                                Generated by Agentic Insight Report Builder · Platform System
                            </Typography>
                        </Box>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default ReportPreviewDialog;
